import { ChevronRight, Schedule } from '@mui/icons-material';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { Avatar, Card, Chip, ClickableSurface, Divider, Row, Stack, Text } from 'library/ui';
import { BusinessSlot } from 'models';
import React from 'react';
import { useHistory } from 'react-router';
import { format12HourTime, formatDateFromDateObject } from 'shared/Dates';
import { ApplicantApprovalSummary } from './BusinessCalendar';

export default function UnfilledShiftsWithApprovals({
    approvalsForJob,
    jobId,
    count,
    slot,
    onViewShifts,
}: {
    approvalsForJob?: ApplicantApprovalSummary;
    jobId: string;
    count: number;
    slot?: BusinessSlot;
    onViewShifts: () => void;
}) {
    const history = useHistory();
    const applicants = approvalsForJob?.applicants ?? [];
    const newApprovalsCount = applicants.filter((a) => !a.viewed).length;
    const awaitingConfirmationCount = applicants.length - newApprovalsCount;
    const goToApplicants = () => {
        const dateParam = slot ? `?date=${formatDateFromDateObject(new Date(slot.start_date))}` : '';
        history.push(`jobs/${jobId}/applicants${dateParam}`);
    };

    const shiftCountLabel = `${count} unfilled shift${count === 1 ? '' : 's'}`;
    const timeRange = slot
        ? `${format12HourTime(slot.start_date, slot.ongoing_request.timezone)} – ${format12HourTime(
              slot.end_date,
              slot.ongoing_request.timezone,
          )}`
        : '';
    const title = slot
        ? `${slot.ongoing_request.headline} · ${count === 1 ? timeRange : shiftCountLabel}`
        : shiftCountLabel;

    return (
        <ShiftCard key={jobId} tone="warning">
            <SectionSurface onClick={onViewShifts}>
                <Stack gap="none">
                    <Text size="xs" weight="bold" tone="warning" uppercase>
                        Open shift{count === 1 ? '' : 's'}
                    </Text>
                    <Text weight="bold">{title}</Text>
                </Stack>
            </SectionSurface>
            {newApprovalsCount > 0 && (
                <>
                    <Divider />
                    <SectionSurface onClick={goToApplicants}>
                        <Row gap="sm" align="center" fullWidth>
                            <Stack gap="sm" grow>
                                <Row gap="sm" align="center">
                                    <AvatarCluster applicants={applicants.filter((a) => !a.viewed)} />
                                    <Chip variant="filled" size="small" bgColor={Colors.warning}>
                                        New!
                                    </Chip>
                                </Row>
                                <Stack gap="none">
                                    <Text weight="bold">
                                        {newApprovalsCount} interested substitute{newApprovalsCount === 1 ? '' : 's'}
                                    </Text>
                                    <Text size="sm" tone="secondary">
                                        {approvalsForJob?.manual_clearinghouse_check
                                            ? 'Book now'
                                            : 'Near matches · review to fill'}
                                    </Text>
                                </Stack>
                            </Stack>
                            <ChevronRight htmlColor={Colors.warning} />
                        </Row>
                    </SectionSurface>
                </>
            )}
            {awaitingConfirmationCount > 0 && (
                <>
                    <Divider />
                    <SectionSurface onClick={goToApplicants}>
                        <Row gap="sm" align="center" fullWidth>
                            <IconBadge>
                                <Schedule htmlColor={Colors.warning} fontSize="small" />
                            </IconBadge>
                            <Stack gap="none" grow>
                                <Text weight="bold">
                                    {awaitingConfirmationCount} approval{awaitingConfirmationCount === 1 ? '' : 's'}{' '}
                                    pending
                                </Text>
                                <Text size="sm" tone="secondary">
                                    Awaiting acceptance
                                </Text>
                            </Stack>
                            <ChevronRight htmlColor={Colors.warning} />
                        </Row>
                    </SectionSurface>
                </>
            )}
        </ShiftCard>
    );
}

function AvatarCluster({ applicants }: { applicants: ApplicantApprovalSummary['applicants'] }) {
    return (
        <Row gap="none" align="center">
            {applicants.map((applicant, i) =>
                applicant.applied_user.pictureUrl ? (
                    <ClusterAvatar
                        key={applicant.id}
                        imageUrl={applicant.applied_user.pictureUrl}
                        name={applicant.applied_user.name}
                        size={26}
                        $overlap={i > 0}
                    />
                ) : (
                    <AvatarCircle key={applicant.id} $tint={AVATAR_TINTS[i % AVATAR_TINTS.length]} $overlap={i > 0} />
                ),
            )}
        </Row>
    );
}

const WARNING_SOFT = '#F8EDDE';
const AVATAR_TINTS = ['#EFCEC5', '#DB9784', Colors.warning];

const ClusterAvatar = styled(Avatar, { shouldForwardProp: (prop) => prop !== '$overlap' })<{ $overlap: boolean }>(
    ({ $overlap }) => ({
        marginLeft: $overlap ? -10 : 0,
    }),
);

const ShiftCard = styled(Card)({
    padding: 0,
    overflow: 'hidden',
    width: '100%',
});

const SectionSurface = styled(ClickableSurface)({
    borderRadius: 0,
    '&:hover': {
        backgroundColor: WARNING_SOFT,
    },
});

const AvatarCircle = styled('span')<{ $tint: string; $overlap: boolean }>(({ $tint, $overlap }) => ({
    width: 26,
    height: 26,
    borderRadius: '50%',
    backgroundColor: $tint,
    border: `2px solid ${Colors.warningBackground}`,
    marginLeft: $overlap ? -10 : 0,
    flexShrink: 0,
}));

const IconBadge = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: WARNING_SOFT,
    flexShrink: 0,
});
